/**
 * @file    widgetBuilder.ts
 * @scope   SHARED
 * @summary Builds the `options` object applied to a widget when the user switches chart type.
 * @consumes `library` alias catalogue
 * @produces buildDefaultOptions
 * @sideEffects None.
 */
import { library } from 'components';

/**
 * Merges the options of the newly selected widget with the ones already configured.
 *
 * Rules preserved from the original implementation:
 * - DataGrid selections keep the user's options; other chart switches reset to the widget defaults.
 * - DataGrid additionally forces `table.showTotals` on for the `OneQuestionManyCalculations` rule.
 *
 * WHY (known quirk, intentionally preserved): the first comparison evaluates
 * `library.DYNAMIC_TABLE_OLD || library.DYNAMIC_TABLE`, i.e. it only ever compares against
 * `DYNAMIC_TABLE_OLD`. Changing it would alter which options survive a chart switch, so it is
 * kept as-is until a dedicated behavioural fix is scheduled.
 * TODO(diy-options-quirk): revisit once regression coverage for chart switching exists.
 *
 * @param selectedWidget Widget template of the newly selected chart type.
 * @param customWidget   Widget currently being edited.
 * @param optionsAlias   Options lookup key (alias or business rule) — see `getOptionsByAlias`.
 * @returns The options object to assign to the rebuilt widget.
 */
export const buildDefaultOptions = (
  selectedWidget: any,
  customWidget: any,
  optionsAlias: string
): any => {
  const isSameChart =
    optionsAlias === (library.DYNAMIC_TABLE_OLD || library.DYNAMIC_TABLE) ||
    customWidget.alias === selectedWidget.alias;

  const newOptions: any = isSameChart ? customWidget.options : selectedWidget.options;

  if (
    selectedWidget.alias === library.DYNAMIC_TABLE ||
    selectedWidget.alias === library.DYNAMIC_TABLE_OLD
  ) {
    return {
      ...newOptions,
      table: {
        ...newOptions.table,
        showTotals: optionsAlias === 'OneQuestionManyCalculations' ? true : false,
      },
    };
  }

  return newOptions;
};
